import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Brain, Sparkles, Calendar, Bot, Loader2, Target, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const SmartPlanning = ({ project }) => {
  const [strategy, setStrategy] = useState(null);
  const [workplan, setWorkplan] = useState(null);
  const [agents, setAgents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [assigningAgent, setAssigningAgent] = useState(null);

  useEffect(() => {
    fetchData();
  }, [project.id]);

  const fetchData = async () => {
    try {
      const [agentsRes, tasksRes] = await Promise.all([
        axios.get(`${API}/projects/${project.id}/agents`),
        axios.get(`${API}/projects/${project.id}/workplan-tasks`)
      ]);
      setAgents(agentsRes.data);
      setTasks(tasksRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateStrategy = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/ai/generate-strategy`, {
        project_id: project.id,
        analysis_type: 'migration_strategy',
        context: {
          project_id: project.id,
          name: project.name,
          source_system: project.source_system,
          target_system: project.target_system,
          migration_type: project.migration_type,
          custom_code_count: 50,
          transaction_volume: 100000,
          data_volume_gb: 500,
          users_count: 250,
          modules: ['FI', 'CO', 'SD', 'MM', 'PP']
        }
      });
      setStrategy(response.data.result.migration_strategy);
    } catch (error) {
      console.error('Error generating strategy:', error);
      alert('Failed to generate strategy');
    } finally {
      setGenerating(false);
    }
  };

  const generateWorkplan = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/ai/generate-workplan`, {
        project_id: project.id,
        analysis_type: 'workplan',
        context: {
          project: {
            project_id: project.id,
            name: project.name,
            migration_type: project.migration_type,
            status: project.status
          },
          analysis: {
            code_objects: 50,
            high_priority: 12,
            simplification_items: 15,
            total_effort_days: 180
          }
        }
      });
      setWorkplan(response.data.result.workplan);
    } catch (error) {
      console.error('Error generating workplan:', error);
      alert('Failed to generate workplan');
    } finally {
      setGenerating(false);
    }
  };

  const assignAgentToTask = async (taskId, agentId) => {
    setAssigningAgent(taskId);
    try {
      const task = tasks.find(t => t.id === taskId);
      await axios.post(`${API}/agents/${agentId}/assign-task?task_id=${taskId}`, task);
      alert('Agent assigned successfully!');
      await fetchData();
    } catch (error) {
      console.error('Error assigning agent:', error);
      alert('Failed to assign agent');
    } finally {
      setAssigningAgent(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Brain className="w-12 h-12" />
            <div>
              <h2 className="text-3xl font-bold">Smart Planning & Agentic Orchestration</h2>
              <p className="text-indigo-100 mt-1">AI-powered migration planning with autonomous agent execution</p>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={generateStrategy}
              disabled={generating}
              className="px-6 py-3 bg-white text-purple-700 rounded-xl hover:bg-purple-50 transition-colors font-medium flex items-center space-x-2 disabled:opacity-50"
            >
              <Sparkles className="w-5 h-5" />
              <span>Generate Strategy</span>
            </button>
            <button
              onClick={generateWorkplan}
              disabled={generating}
              className="px-6 py-3 bg-white/20 hover:bg-white/30 rounded-xl transition-colors font-medium flex items-center space-x-2 disabled:opacity-50"
            >
              <Calendar className="w-5 h-5" />
              <span>Generate Workplan</span>
            </button>
          </div>
        </div>
      </div>

      {strategy && (
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
            <Target className="w-6 h-6 mr-3 text-purple-600" />
            AI-Generated Migration Strategy
          </h3>
          <div className="prose max-w-none">
            <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200">
              <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans">{strategy}</pre>
            </div>
          </div>
        </div>
      )}

      {workplan && (
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
            <Calendar className="w-6 h-6 mr-3 text-blue-600" />
            AI-Generated Workplan
          </h3>
          <div className="p-6 bg-blue-50 rounded-xl border-2 border-blue-200">
            <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans">{workplan}</pre>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <Users className="w-6 h-6 mr-3 text-green-600" />
          Agentic Task Assignment
        </h3>

        {agents.length === 0 ? (
          <div className="text-center py-12 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
            <Bot className="w-16 h-16 mx-auto mb-4 text-yellow-600" />
            <p className="text-lg font-semibold text-gray-900 mb-2">No agents available</p>
            <p className="text-gray-600 mb-4">Create agents in the Agentic AI Studio to enable autonomous task execution</p>
          </div>
        ) : (
          <div className="space-y-4">
            {tasks.slice(0, 10).map((task) => {
              const priorityClass = task.priority === 'critical' ? 'bg-red-100 text-red-700' :
                task.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                'bg-gray-100 text-gray-700';
              
              return (
                <div key={task.id} className="border-2 border-gray-200 rounded-xl p-4 hover:border-blue-400 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-lg font-bold text-gray-900">{task.title}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${priorityClass}`}>
                          {task.priority}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{task.description}</p>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="text-gray-600">Effort: <strong>{task.effort_hours}h</strong></span>
                        <span className="text-gray-600">Type: <strong className="capitalize">{task.task_type}</strong></span>
                        <span className="text-gray-600">Status: <strong className="capitalize">{task.status}</strong></span>
                      </div>
                    </div>

                    <div className="ml-4">
                      <select
                        onChange={(e) => e.target.value && assignAgentToTask(task.id, e.target.value)}
                        disabled={assigningAgent === task.id}
                        className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
                      >
                        <option value="">Assign Agent...</option>
                        {agents.filter(a => a.status === 'idle').map((agent) => (
                          <option key={agent.id} value={agent.id}>{agent.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {generating && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            <Loader2 className="w-16 h-16 text-purple-600 animate-spin mx-auto mb-4" />
            <p className="text-xl font-semibold text-gray-900">AI is generating your plan...</p>
            <p className="text-gray-600 mt-2">This may take a few moments</p>
          </div>
        </div>
      )}
    </div>
  );
};
